import React, { useState } from 'react';
import { Box, Typography, TextField, InputAdornment, Paper } from '@mui/material';
import { useConfig } from '../../../context/ConfigContext';
import { computeRaiseImpact } from '../domain/computeRaiseImpact';
import { solveGrossForTargetNet } from '../domain/solveGrossForTargetNet';
import { PercentageRaisePanel } from './PercentageRaisePanel';
import { TargetIncreasePanel } from './TargetIncreasePanel';
import { ComparisonTable } from './ComparisonTable';

const generateComparisonData = (currentSalary, context) => {
  const comparisonData = [];
  
  for (let pct = 1; pct <= 10; pct++) {
    try {
      const result = computeRaiseImpact({
        currentAnnualGross: currentSalary,
        raisePct: pct,
        context
      });

      // Simple threshold detection based on marginal rate changes
      // This is a heuristic approach since we don't have direct access to 
      // engine metadata about thresholds crossed
      const thresholdBadges = [];
      
      // Detect significant changes in keep-per-pound ratio compared to 1% raise
      if (pct > 1) {
        const prevResult = comparisonData[pct - 2]; // pct-1 index for previous
        if (prevResult) {
          const rateDrop = prevResult.keepPerPound - result.keepPerPound;
          if (rateDrop >= 0.05) { // 5 percentage point drop
            // This is a heuristic - in a real implementation you'd want
            // the engine to provide threshold crossing information
            if (currentSalary + (currentSalary * pct / 100) > 50000) {
              thresholdBadges.push('Higher Rate');
            }
          }
        }
      }

      comparisonData.push({
        raisePct: pct,
        grossDelta: result.deltaGrossAnnual,
        netDeltaAnnual: result.deltaNetAnnual,
        netDeltaMonthly: result.deltaNetMonthly,
        effectiveNetPct: result.effectiveRaisePctNet * 100, // Convert to percentage
        keepPerPound: result.keepPerPound,
        thresholdBadges
      });
    } catch (error) {
      console.error(`Error calculating ${pct}% raise:`, error);
      // Continue with other percentages
    }
  }
  
  return comparisonData;
};

export const PayRiseForm = ({ currentSalary: propCurrentSalary = 50000 }) => {
  const { config } = useConfig();
  const [currentSalary, setCurrentSalary] = useState(propCurrentSalary);
  const [percentageResults, setPercentageResults] = useState(null);
  const [targetResults, setTargetResults] = useState(null);
  const [comparisonData, setComparisonData] = useState([]);
  const [loadingState, setLoadingState] = useState({
    percentage: false,
    target: false,
    comparison: false
  });
  const [errors, setErrors] = useState({
    percentage: null,
    target: null,
    comparison: null
  });

  // Build context object for the engine
  const buildContext = () => ({
    config,
    // Add other context from ConfigContext if available
    // studentLoanPlan, pension, etc.
  });

  const handlePercentageCalculation = async (input) => {
    setLoadingState(prev => ({ ...prev, percentage: true }));
    setErrors(prev => ({ ...prev, percentage: null }));

    try {
      const context = buildContext();
      const result = computeRaiseImpact({
        ...input,
        context
      });
      
      setPercentageResults(result);
      
      // Also generate comparison data when calculating percentage
      const comparison = generateComparisonData(currentSalary, context);
      setComparisonData(comparison);
      
    } catch (error) {
      console.error('Percentage calculation error:', error);
      setErrors(prev => ({ 
        ...prev, 
        percentage: error.message || 'Unable to calculate raise impact' 
      }));
    } finally {
      setLoadingState(prev => ({ ...prev, percentage: false }));
    }
  };

  const handleTargetCalculation = async (input) => {
    setLoadingState(prev => ({ ...prev, target: true }));
    setErrors(prev => ({ ...prev, target: null }));

    try {
      const context = buildContext();
      const result = solveGrossForTargetNet({
        ...input,
        context
      });
      
      setTargetResults(result);
      
    } catch (error) {
      console.error('Target calculation error:', error);
      let errorMessage = 'Unable to calculate required raise';
      
      if (error.message.includes('Target too high')) {
        errorMessage = 'Target too high for given constraints';
      } else if (error.message.includes('greater than zero')) {
        errorMessage = 'Target must be greater than zero';
      }
      
      setErrors(prev => ({ 
        ...prev, 
        target: errorMessage
      }));
    } finally {
      setLoadingState(prev => ({ ...prev, target: false }));
    }
  };

  const handleSalaryChange = (event) => {
    const value = parseFloat(event.target.value) || 0;
    setCurrentSalary(value);
    
    // Clear previous results when salary changes
    setPercentageResults(null);
    setTargetResults(null);
    setComparisonData([]);
    setErrors({
      percentage: null,
      target: null,
      comparison: null
    });
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
        Pay Rise Simulator
      </Typography>
      
      <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', mb: 4 }}>
        Understand the real after-tax impact of salary increases and negotiate more effectively.
      </Typography>

      {/* Salary Input */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, maxWidth: 400, mx: 'auto' }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
          Current Annual Salary
        </Typography>
        
        <TextField
          fullWidth
          type="number"
          value={currentSalary}
          onChange={handleSalaryChange}
          InputProps={{
            startAdornment: <InputAdornment position="start">£</InputAdornment>,
          }}
          inputProps={{
            min: 1,
            step: 1000,
            'aria-label': 'Current annual salary'
          }}
          sx={{ mt: 2 }}
          placeholder="Enter your current salary"
        />
        
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: 'center' }}>
          Enter your gross annual salary to see raise impact calculations
        </Typography>
      </Paper>

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        <PercentageRaisePanel
          currentSalary={currentSalary}
          onCalculate={handlePercentageCalculation}
          results={percentageResults}
          loading={loadingState.percentage}
          error={errors.percentage}
        />

        <TargetIncreasePanel
          currentSalary={currentSalary}
          onCalculate={handleTargetCalculation}
          results={targetResults}
          loading={loadingState.target}
          error={errors.target}
        />
      </Box>

      {comparisonData.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <ComparisonTable
            currentSalary={currentSalary}
            comparisonData={comparisonData}
            loading={loadingState.comparison}
          />
        </Box>
      )}
    </Box>
  );
};